// Frozen per-cell variable values for one simulated Transcript. The API has
// already applied tenant scope and redacted sensitive values.

import type { components } from "../api/schema";
import { docUrl } from "../lib/docLinks";
import { EmptyState, HelpTip, InlineError, Section, Skeleton } from "../ui";
import {
  ResolvedCellView,
  type ResolvedCellSupplierMap,
} from "./variables/ResolvedCellView";
type TranscriptVariablesResponse =
  components["schemas"]["TranscriptVariablesResponse"];

export interface TranscriptVariablesProps {
  errorMessage?: string | null;
  isPending?: boolean;
  response?: TranscriptVariablesResponse | null;
  suppliers?: ResolvedCellSupplierMap;
}

export function TranscriptVariables({
  errorMessage,
  isPending = false,
  response = null,
  suppliers,
}: TranscriptVariablesProps) {
  const variableCount = Object.keys(response?.variables ?? {}).length;
  const unavailableMessage =
    response?.notice ?? "Frozen variables are unavailable for this Transcript.";

  return (
    <Section
      aria-label="Frozen variables"
      hairline
      heading="Frozen variables"
      help={
        <HelpTip
          docHref={docUrl("guides/use-variables-in-simulations")}
          docLabel="Read simulation variables"
          label="About frozen variables"
        >
          {
            "These are the values saved for this simulation case during setup. Supplied by identifies the winning source. Later edits to defaults, Personas, Scenarios, rows, or templates do not change this Transcript's inputs. Supplier links open the current editor."
          }
        </HelpTip>
      }
    >
      {isPending ? (
        <Skeleton label="Loading frozen variables" rows={3} shape="table-row" />
      ) : null}
      {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
      {!isPending && !errorMessage && response?.status === "unavailable" ? (
        <InlineError>{unavailableMessage}</InlineError>
      ) : null}
      {!isPending && !errorMessage && response?.status === "not_applicable" ? (
        <EmptyState title="Frozen variables do not apply" />
      ) : null}
      {!isPending &&
      !errorMessage &&
      response?.status === "available" &&
      variableCount === 0 ? (
        <EmptyState title="No frozen variables" />
      ) : null}
      {!isPending && !errorMessage && variableCount > 0 ? (
        <ResolvedCellView
          suppliers={suppliers}
          variables={response?.variables ?? {}}
        />
      ) : null}
    </Section>
  );
}
